import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { Car, Phone, Star, MapPin, RefreshCw, Plus, Plane } from 'lucide-react';
import { getDrivers, getPendingRides } from '../../services/api';
import { GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_OPTIONS, AIRPORT_TERMINAL, GOOGLE_MAPS_API_KEY } from '../../config/googleMaps';
import { getCarMarkerIcon, getLocationPinIcon, CAR_STATUS_COLORS } from '../../utils/carMarkers';
import useWebSocket from '../../hooks/useWebSocket';

const mapContainerStyle = {
  width: '100%',
  height: '100%',
  minHeight: '500px'
};

// Smooth animation helper
const animateMarkerTo = (marker, newPosition, duration = 1000) => {
  if (!marker || !marker.getPosition) return;
  
  const startPos = marker.getPosition();
  const startLat = startPos.lat();
  const startLng = startPos.lng();
  const endLat = newPosition.lat;
  const endLng = newPosition.lng;
  
  const startTime = Date.now();
  
  const animate = () => {
    const elapsed = Date.now() - startTime;
    const progress = Math.min(elapsed / duration, 1);
    
    // Easing function for smooth animation
    const easeProgress = 1 - Math.pow(1 - progress, 3);
    
    const currentLat = startLat + (endLat - startLat) * easeProgress;
    const currentLng = startLng + (endLng - startLng) * easeProgress;
    
    marker.setPosition({ lat: currentLat, lng: currentLng });
    
    if (progress < 1) {
      requestAnimationFrame(animate);
    }
  };
  
  requestAnimationFrame(animate);
};

export const LiveMap = ({ onDriverSelect, onRideSelect, onCreateBooking }) => {
  const [drivers, setDrivers] = useState([]);
  const [pendingRides, setPendingRides] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedRide, setSelectedRide] = useState(null);
  const [loading, setLoading] = useState(true);
  const mapRef = useRef(null);
  const markersRef = useRef({}); // Store marker refs for animation
  const prevDriverPositions = useRef({}); // Track previous positions
  
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES
  });

  const [mapCenter] = useState(AIRPORT_TERMINAL);

  const applyDriverPosition = useCallback((driverId, position) => {
    if (!driverId || position?.lat == null || position?.lng == null) return;
    const nextPosition = { lat: position.lat, lng: position.lng };

    setDrivers((prev) => {
      const exists = prev.some((d) => d.id === driverId);
      if (!exists) return prev;
      return prev.map((d) => (d.id === driverId ? { ...d, position: nextPosition } : d));
    });

    if (prevDriverPositions.current[driverId]) {
      prevDriverPositions.current[driverId] = { ...nextPosition };
    }

    setSelectedDriver((prev) =>
      prev?.id === driverId ? { ...prev, position: nextPosition } : prev
    );
  }, []);

  const handleWebSocketMessage = useCallback((message) => {
    const locationTypes = [
      'driver.location.updated',
      'driver_location_updated',
      'driver_moved',
      'driver_location',
    ];
    if (!locationTypes.includes(message.type)) return;
    const driverId = message.driver_id;
    const position = message.position || { lat: message.lat, lng: message.lng };
    applyDriverPosition(driverId, position);
  }, [applyDriverPosition]);

  useWebSocket('dispatcher', handleWebSocketMessage);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [driversData, ridesData] = await Promise.all([
        getDrivers(),
        getPendingRides()
      ]);
      
      // Animate markers to new positions
      driversData.filter(d => d.position).forEach(driver => {
        const prevPos = prevDriverPositions.current[driver.id];
        const marker = markersRef.current[driver.id];
        
        if (prevPos && marker && driver.position) {
          const hasMoved = 
            Math.abs(prevPos.lat - driver.position.lat) > 0.00001 ||
            Math.abs(prevPos.lng - driver.position.lng) > 0.00001;
          
          if (hasMoved) {
            animateMarkerTo(marker, driver.position, 1000);
          }
        }
        
        // Update previous position
        if (driver.position) {
          prevDriverPositions.current[driver.id] = { ...driver.position };
        }
      });
      
      setDrivers(driversData.filter(d => d.position));
      setPendingRides(ridesData);
    } catch (error) {
      console.error('Error fetching map data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Poll every 5 seconds for live updates
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, []);

  // Map bounds fitting removed - user controls map manually

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // Use the new car marker icons
  const getDriverMarkerIconForMap = (status) => {
    return getCarMarkerIcon(status);
  };

  const getRideMarkerIcon = (type = 'pickup') => {
    return getLocationPinIcon(type);
  };

  const getAirportMarkerIcon = () => {
    // Airport terminal marker - blue with plane icon
    const airportSvg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="48" viewBox="0 0 40 48">
        <ellipse cx="20" cy="46" rx="8" ry="2" fill="rgba(0,0,0,0.2)"/>
        <path d="M20,0 C9,0 0,9 0,20 C0,35 20,46 20,46 C20,46 40,35 40,20 C40,9 31,0 20,0 Z" fill="#1e40af"/>
        <circle cx="20" cy="19" r="14" fill="#3b82f6"/>
        <path d="M20 8 L23 16 L31 16 L25 21 L27 29 L20 24 L13 29 L15 21 L9 16 L17 16 Z" fill="white"/>
      </svg>
    `;
    return {
      url: `data:image/svg+xml,${encodeURIComponent(airportSvg.trim())}`,
      scaledSize: { width: 40, height: 48 },
      anchor: { x: 20, y: 48 }
    };
  };

  const handleDriverClick = (driver) => {
    setSelectedDriver(driver);
    setSelectedRide(null);
    if (onDriverSelect) onDriverSelect(driver);
  };

  const handleRideClick = (ride) => {
    setSelectedRide(ride);
    setSelectedDriver(null);
    if (onRideSelect) onRideSelect(ride);
  };

  if (loadError) {
    return (
      <div className="map-container bg-gray-100 flex items-center justify-center" style={{ minHeight: '500px' }}>
        <div className="text-center">
          <MapPin className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-gray-600">Gabim në ngarkimin e hartës</p>
          <p className="text-sm text-gray-400">Kontrolloni API key të Google Maps</p>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="map-container bg-gray-100 flex items-center justify-center" style={{ minHeight: '500px' }}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Duke ngarkuar hartën...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="map-container relative" style={{ minHeight: '500px' }}>
      {/* Refresh indicator */}
      {loading && (
        <div className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-md">
          <RefreshCw className="w-4 h-4 text-gray-400 animate-spin" />
        </div>
      )}

      {/* Create Booking Button - Nice style with icon */}
      <button
        onClick={onCreateBooking}
        className="absolute top-4 right-4 z-10 flex flex-col items-center justify-center px-5 py-3 rounded-xl shadow-lg font-semibold transition-all hover:scale-105 hover:shadow-xl"
        style={{ 
          background: '#dc2313', 
          color: '#fff',
          minWidth: '100px'
        }}
        data-testid="create-booking-btn"
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mb-1">
          <rect x="3" y="8" width="18" height="10" rx="2" />
          <circle cx="7" cy="18" r="2" />
          <circle cx="17" cy="18" r="2" />
          <path d="M5 8V6a2 2 0 012-2h10a2 2 0 012 2v2" />
          <path d="M12 4v4" />
          <path d="M9 12h6" />
          <path d="M12 9v6" />
        </svg>
        <span className="text-sm font-bold">Krijo Porosi</span>
      </button>
      
      {/* Stats overlay */}
      <div className="absolute top-4 left-4 z-10 bg-white rounded-lg p-3 shadow-md">
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span>{drivers.filter(d => d.status === 'online').length} online</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ background: '#fabc0c' }} />
            <span>{drivers.filter(d => d.status === 'busy').length} busy</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ background: '#dc2313' }} />
            <span>{pendingRides.length} porosi</span>
          </div>
        </div>
      </div>

      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={mapCenter}
        zoom={15}
        onLoad={onMapLoad}
        options={GOOGLE_MAPS_OPTIONS}
      >
        {/* Airport Terminal Marker - Central Office */}
        <Marker
          position={AIRPORT_TERMINAL}
          icon={getAirportMarkerIcon()}
          title="Zyra Qendrore - Aeroporti Nënë Tereza"
          zIndex={1000}
        />

        {/* Driver Markers with smooth animation */}
        {drivers.map((driver) => (
          <Marker
            key={driver.id}
            position={{ lat: driver.position.lat, lng: driver.position.lng }}
            icon={getDriverMarkerIconForMap(driver.status)}
            onClick={() => handleDriverClick(driver)}
            title={driver.name}
            onLoad={(marker) => {
              markersRef.current[driver.id] = marker;
            }}
            onUnmount={() => {
              delete markersRef.current[driver.id];
            }}
          />
        ))}

        {/* Pending Ride Pickup Markers - Pin style */}
        {pendingRides.map((ride) => (
          <Marker
            key={`pickup-${ride.id}`}
            position={{ 
              lat: ride.pickup?.position?.lat || 0, 
              lng: ride.pickup?.position?.lng || 0 
            }}
            icon={getRideMarkerIcon()}
            onClick={() => handleRideClick(ride)}
            title={`Pritje: ${ride.passenger_name}`}
          />
        ))}

        {/* Driver Info Window */}
        {selectedDriver && (
          <InfoWindow
            position={{ lat: selectedDriver.position.lat, lng: selectedDriver.position.lng }}
            onCloseClick={() => setSelectedDriver(null)}
          >
            <div className="p-2 min-w-[200px]">
              <div className="flex items-center gap-3 mb-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                  style={{ background: 'linear-gradient(135deg, #FBBF24, #F59E0B)' }}
                >
                  {selectedDriver.avatar}
                </div>
                <div>
                  <h4 className="font-semibold text-sm">{selectedDriver.name}</h4>
                  <p className="text-xs text-gray-500">{selectedDriver.vehicle?.model}</p>
                </div>
              </div>
              <div className="space-y-2 text-xs">
                <div className="flex items-center gap-2">
                  <Car className="w-3 h-3 text-gray-400" />
                  <span>{selectedDriver.vehicle?.plate}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-3 h-3 text-gray-400" />
                  <span>{selectedDriver.phone}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-3 h-3 text-yellow-400" />
                  <span>{selectedDriver.rating} ({selectedDriver.total_rides} udhëtime)</span>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t">
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                  selectedDriver.status === 'online' ? 'bg-green-100 text-green-600' :
                  selectedDriver.status === 'busy' ? 'bg-amber-50 text-amber-600' : 'bg-gray-100 text-gray-600'
                }`}>
                  {selectedDriver.status === 'online' ? 'I Lirë' :
                   selectedDriver.status === 'busy' ? 'Në Udhëtim' : 'Offline'}
                </span>
              </div>
            </div>
          </InfoWindow>
        )}

        {/* Ride Info Window */}
        {selectedRide && (
          <InfoWindow
            position={{ 
              lat: selectedRide.pickup?.position?.lat || 0, 
              lng: selectedRide.pickup?.position?.lng || 0 
            }}
            onCloseClick={() => setSelectedRide(null)}
          >
            <div className="p-2 min-w-[220px]">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-medium px-2 py-1 rounded-full" style={{ background: 'rgba(220, 35, 19, 0.1)', color: '#dc2313' }}>
                  Në Pritje
                </span>
                <span className="text-xs text-gray-500">#{selectedRide.id.substring(0, 8)}</span>
              </div>
              <div className="space-y-2 text-xs">
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500 mt-1" />
                  <span className="flex-1">{selectedRide.pickup?.address}</span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500 mt-1" />
                  <span className="flex-1">{selectedRide.dropoff?.address}</span>
                </div>
                <div className="flex items-center justify-between pt-2 border-t">
                  <span className="text-gray-500">{selectedRide.passenger_name}</span>
                  <span className="font-semibold">{selectedRide.estimated_price} Lekë</span>
                </div>
              </div>
              <button 
                className="w-full mt-3 text-xs py-2 rounded-lg font-semibold"
                style={{ background: 'linear-gradient(135deg, #fabc0c, #e0a80a)', color: '#1b1c1b' }}
                onClick={() => onRideSelect && onRideSelect(selectedRide)}
              >
                Cakto Shofer
              </button>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  );
};

export default LiveMap;
